import { useMemo } from 'react'
import { deriveSearchableAppPages } from '@/config/appNavigation'
import { useAppNavigation } from '@features/navigation'
import { useGlobalSearch, useSearchPreview } from '@features/tables/hooks/useGlobalSearch'
import { cellMatches, type MatchMode } from '@features/tables/logic/tableSearch'
import type { SearchEntityHit, SearchMetadataKind, SearchValueHit } from '../models'
import { ENTITY_FILTERS, type FilterKey, type ResultKind } from '../logic/constants'
import { buildGlobalSearchSections } from '../logic/globalSearchSections'
import { buildRecordRows } from '../logic/rows'
import { getSearchMatchKey, getSearchMatchParams } from '../logic/searchMatch'
import { useMetadataSectionPages } from './useMetadataSectionPages'
import { usePreviewRecordPages } from './usePreviewRecordPages'

const NO_HITS: SearchEntityHit[] = []
const NO_VALUES: SearchValueHit[] = []

interface GlobalSearchResultsOptions {
  /** The settled (debounced) search text; empty shows the preview. */
  query: string
  matchMode: MatchMode
  activeFilter: FilterKey
}

/**
 * Everything the palette shows for a search: the response (or the empty-search
 * preview), the matching app pages, the All tab's "See more" pages, and the
 * sections, tab counts, and tabs built from them.
 */
export function useGlobalSearchResults({
  query,
  matchMode,
  activeFilter,
}: GlobalSearchResultsOptions) {
  const matchParams = useMemo(() => getSearchMatchParams(matchMode), [matchMode])
  // A search is its text and how it matches; a new one starts its pages over.
  const scope = `${query}\u0000${getSearchMatchKey(matchMode)}`
  const isPreview = query.trim().length === 0
  const search = useGlobalSearch(query, matchParams)
  const preview = useSearchPreview(isPreview)
  const data = isPreview ? preview.data : search.data
  const isLoading = isPreview ? preview.isLoading : search.isLoading

  const { modules } = useAppNavigation()
  // Only pages in modules the user may open are offered.
  const searchablePages = useMemo(() => deriveSearchableAppPages(modules), [modules])
  const pages = useMemo(() => {
    if (isPreview) return searchablePages
    return searchablePages.filter(
      (page) =>
        cellMatches(page.name, query, matchMode) || cellMatches(page.module, query, matchMode)
    )
  }, [query, isPreview, matchMode, searchablePages])

  const firstMetadataRows = useMemo<Record<SearchMetadataKind, SearchEntityHit[]>>(
    () => ({
      tables: data?.tables ?? NO_HITS,
      views: data?.views ?? NO_HITS,
      fields: data?.fields ?? NO_HITS,
    }),
    [data]
  )
  const metadataTotals = useMemo<Record<SearchMetadataKind, number>>(
    () => ({
      tables: data?.counts?.tables ?? firstMetadataRows.tables.length,
      views: data?.counts?.views ?? firstMetadataRows.views.length,
      fields: data?.counts?.fields ?? firstMetadataRows.fields.length,
    }),
    [data, firstMetadataRows]
  )
  const pagingOptions = { query, matchParams, scope }
  const metadataPages = {
    tables: useMetadataSectionPages({
      ...pagingOptions,
      kind: 'tables',
      firstRows: firstMetadataRows.tables,
      total: data?.counts?.tables,
    }),
    views: useMetadataSectionPages({
      ...pagingOptions,
      kind: 'views',
      firstRows: firstMetadataRows.views,
      total: data?.counts?.views,
    }),
    fields: useMetadataSectionPages({
      ...pagingOptions,
      kind: 'fields',
      firstRows: firstMetadataRows.fields,
      total: data?.counts?.fields,
    }),
  }

  const firstRecordRows = useMemo(() => buildRecordRows(data?.groups ?? [], query), [data, query])
  const recordPages = usePreviewRecordPages({
    enabled: isPreview,
    scope,
    total: data?.counts?.records ?? data?.total_matches ?? firstRecordRows.length,
  })
  const recordRows = recordPages.rows.length > 0 ? recordPages.rows : firstRecordRows
  const previewRecordTotal =
    recordPages.total ?? data?.counts?.records ?? data?.total_matches ?? recordRows.length
  const recordCount = isPreview
    ? previewRecordTotal
    : (data?.counts?.records ?? data?.total_matches ?? recordRows.length)
  const values = data?.values ?? NO_VALUES
  const valueCount = data?.counts?.values ?? values.length

  const tables = metadataPages.tables.source
  const views = metadataPages.views.source
  const fields = metadataPages.fields.source
  const sections = useMemo(
    () =>
      buildGlobalSearchSections({
        activeFilter,
        pages,
        metadata: { tables, views, fields },
        records: { rows: recordRows, count: recordCount },
        values: { rows: values, count: valueCount },
      }),
    [activeFilter, pages, tables, views, fields, recordRows, recordCount, values, valueCount]
  )

  const counts: Record<ResultKind, number> = {
    pages: pages.length,
    tables: metadataTotals.tables,
    views: metadataTotals.views,
    fields: metadataTotals.fields,
    records: isPreview
      ? (recordPages.total ?? data?.counts?.records ?? recordRows.length)
      : (data?.counts?.records ?? data?.total_matches ?? 0),
    values: isPreview ? previewRecordTotal : valueCount,
  }

  const visibleFilters = useMemo(() => {
    const availableRows: Record<ResultKind, number> = {
      pages: pages.length,
      tables: tables.rows.length,
      views: views.rows.filter((view) => view.table_id).length,
      fields: fields.rows.filter((field) => field.table_id).length,
      records: recordRows.length,
      values: isPreview ? 1 : values.length,
    }
    // While a refined search loads, the tab the user is on stays: the
    // search's answer can empty it, the wait for that answer doesn't.
    return ENTITY_FILTERS.filter(
      (key) => key === 'all' || availableRows[key] > 0 || (isLoading && key === activeFilter)
    )
  }, [
    pages,
    tables.rows,
    views.rows,
    fields.rows,
    recordRows,
    isPreview,
    values,
    isLoading,
    activeFilter,
  ])

  return {
    isPreview,
    isLoading,
    matchParams,
    scope,
    sections,
    counts,
    visibleFilters,
    firstMetadataRows,
    metadataTotals,
    firstRecordRows,
    previewRecordTotal,
    /** "See more" under a metadata section: ten more rows, loading pages ahead. */
    revealMore: (kind: SearchMetadataKind) => metadataPages[kind].revealMore(),
    /** "See less" under a metadata section: back to three rows. */
    seeLess: (kind: SearchMetadataKind) => metadataPages[kind].seeLess(),
    /** Expanding the empty search's records: load pages beyond `desiredVisible` rows. */
    loadRecordsAhead: recordPages.loadAhead,
  }
}
